import { DropboxOutlined, RightOutlined } from '@ant-design/icons';
import { MQ } from '@common/constants/mq.constant';
import { THEME } from '@common/constants/theme.constant';
import { ButtonV2 } from '@common/general-ui/button-v2/button-v2.component';
import { Icon } from '@common/general-ui/icon/icon.component';
import { Modal } from '@common/general-ui/modal/modal.component';
import { ITabItem } from '@common/general-ui/tabs/tabs.component';
import { useModalEx } from '@common/hooks/use-modal-ex.hook';
import { t } from '@common/hooks/use-t.hook';
import { IAction } from '@common/interfaces/action.interface';
import { formatPrice } from '@common/utils/format-price/format-price.util';
import { apiHooks } from '@src/api/hooks/api-hooks';
import { IRegisterModel } from '@src/api/model/register.model';
import { Logo } from '@src/shared/layout/logo.component';
import { LINKS } from '@src/shared/links.constant';
import React, { Dispatch, SetStateAction, useCallback } from 'react';
import { css } from 'twin.macro';
import { OrderSummaryContainer } from './order-summary-card/container/order-summary.container';
import { RegisterNavTab } from './register-nav-tab.component';

export enum ERegisterTabNames {
    checkDelivery = 'checkDelivery',
    yourFirstBox = 'yourFirstBox',
    yourAccountDetails = 'yourAccountDetails',
    yourDelivery = 'yourDelivery',
    yourPreferences = 'yourPreferences',
    yourPaymentInfo = 'yourPaymentInfo',
    deliveryDetails = 'deliveryDetails',
}
export const REGISTER_TABS: ITabItem[] = [
    {
        name: ERegisterTabNames.checkDelivery,
        title: t('Address Checker'),
        href: LINKS.register.checkDelivery,
        isVisible: true,
    },
    {
        name: ERegisterTabNames.yourFirstBox,
        title: t('Choose box'),
        href: LINKS.register.firstBox,
        isVisible: true,
    },
    {
        name: ERegisterTabNames.yourAccountDetails,
        title: t('Account'),
        href: LINKS.register.accountDetails,
        isVisible: true,
    },
    {
        name: ERegisterTabNames.deliveryDetails,
        title: t('Delivery'),
        href: LINKS.register.deliveryDetails,
        isVisible: true,
    },
    {
        name: ERegisterTabNames.yourPaymentInfo,
        title: t('Payment'),
        href: LINKS.register.payment,
        isVisible: true,
    },
];

interface IOrderSummaryProps {
    hideModal?: () => void;
    registerInfo: IRegisterModel;
    saveToRegister: (data: Partial<IRegisterModel>) => void;
    setTotalPrice?: Dispatch<SetStateAction<number>>;
}

export type ITabChangeChandler = (tab: ITabItem) => void;

interface IProps {
    activeTab?: ITabItem;
    registerInfo: IRegisterModel;
    saveToRegister: (data: Partial<IRegisterModel>) => void;
    onTabChange?: ITabChangeChandler;
    totalPrice?: number;
    setTotalPrice?: Dispatch<SetStateAction<number>>;
}

export const RegisterHeaderV3 = ({ activeTab, onTabChange, registerInfo, saveToRegister }: IProps): JSX.Element => {
    const { data: orderInfoData } = apiHooks.registration.useGetEstimateOrderInfo(
        {
            plan_item_price_id: registerInfo.priceId,
            delivery_configuration_id: registerInfo.deliveryConfigId,
            coupon_id: registerInfo.couponId,
        },
        { enabled: !!registerInfo.priceId && !!registerInfo.deliveryConfigId },
    );

    const handleLinkClick = useCallback<IAction<ITabItem>>(
        (tab) => {
            if (activeTab !== tab) {
                onTabChange && onTabChange(tab);
            }
        },
        [activeTab, onTabChange],
    );

    const renderTabs = (): JSX.Element[] => {
        return REGISTER_TABS.map((tab, index) => {
            return (
                <RegisterNavTab
                    index={index}
                    isActive={tab.href === activeTab?.href}
                    tab={tab}
                    onClick={handleLinkClick}
                    key={tab.href?.toString()}
                />
            );
        });
    };

    const OrderSummary = useCallback(({ hideModal }: IOrderSummaryProps): JSX.Element => {
        return (
            <Modal
                name="OrderSummary"
                title="Order Summary"
                centered
                css={styles.orderSummaryModal}
                onCloseClick={hideModal}
                variant="tertiary"
                renderFooter={(): JSX.Element => (
                    <ButtonV2 tw="w-full" name="dismiss" variant="secondary" onClick={hideModal}>
                        Close
                    </ButtonV2>
                )}
            >
                <OrderSummaryContainer />
            </Modal>
        );
    }, []);

    const [showModal, hideModal] = useModalEx(() => {
        return <OrderSummary hideModal={hideModal} registerInfo={registerInfo} saveToRegister={saveToRegister} />;
    }, [OrderSummary, registerInfo, saveToRegister]);

    return (
        <>
            <header css={styles.header} tw="fixed flex items-center w-full bg-white top-0 py-3 px-4 lg:px-10 lg:py-4">
                <div css={styles.logo}>
                    <Logo />
                </div>
                <div tw="w-full">
                    <ul tw="flex col-span-6 col-start-4 col-end-9 justify-end lg:gap-2 lg:justify-center">{renderTabs()}</ul>
                </div>
            </header>

            <div css={styles.headerHeightEl}></div>
            {registerInfo.productId && (
                <button
                    onClick={showModal}
                    tw="m-4 mb-0 p-4 flex items-center justify-between lg:hidden text-base font-light"
                    css={styles.orderSummary}
                >
                    <div tw="flex items-center">
                        <Icon tw="mr-2 mt-[-2px]" icon={DropboxOutlined} />
                        <p>
                            <span tw="font-medium">Have a discount code?</span> Tap to add
                        </p>
                        <Icon tw="ml-1 mt-[-2px] text-xs" icon={RightOutlined} />
                    </div>

                    {formatPrice(orderInfoData?.final_amount || 0)}
                </button>
            )}
        </>
    );
};

const styles = {
    header: css`
        z-index: 100;
        border-bottom: 1px solid ${THEME.GRAY_100};
        .but-mob {
            font-size: ${THEME.SMALL_FONT_SIZE};
            padding: 8px 12px;
            height: auto;
            line-height: 21px;
        }
    `,
    headerHeightEl: css`
        height: 60px;
        ${MQ.lg} {
            height: 70px;
        }
    `,
    logo: css`
        width: 107px !important;
        ${MQ.lg} {
            width: 140px !important;
        }
    `,
    orderSummary: css`
        background: ${THEME.WHITE};
        border-radius: 5px;
    `,
    orderSummaryModal: css`
        top: revert;

        display: flex;
        justify-content: center;
        align-items: center;

        .ant-modal-content {
            margin: 10px;
        }
        .ant-modal-wrap {
            display: flex !important;
            justify-content: center;
            align-items: center;
        }

        .ant-modal-title {
            text-align: center;
        }

        && .ant-modal-body {
            padding: 24px;
            overflow-y: auto;
            max-height: calc(100vh - 212px);
        }
    `,
};
